import React, { useState, useMemo } from "react";
import {
  ActionType,
  CommonActionBar,
  DEFAULT_ACTION_STYLES,
} from "../../components/ActionBar";
import HighlightDonutChart from "./HighlightDonutChart";
import * as S from "./VoiceHighlightPanel.styles";
// import { useProcessVhStore } from "../../../../../store/processVhStore"; // 삭제
import HighlightPlayer, { MarkerData } from "./HighlightPlayer";
import { PanelContainer } from "../ConversationPanel/ConversationPanel.styles";
import { ContentContainer } from "../AudioAnalysisPanel/AudioAnalysisPanel.styles";
import {
  ProcessResultVh as VHResult,
  Mllm,
  OutputUtterance,
  Outlier,
} from "types/output_vh";

interface VoiceHighlightPanelProps {
  result: VHResult | null;
  onDownload: () => void;
  onLike: () => void;
  onDislike: () => void;
  feedbackStatus: "positive" | "negative" | null;
}

const VoiceHighlightPanel: React.FC<VoiceHighlightPanelProps> = ({
  result,
  onDownload,
  onLike,
  onDislike,
  feedbackStatus,
}) => {
  const [activeMarkerType, setActiveMarkerType] = useState<string | null>(null);
  const [isHighlightVisible, setIsHighlightVisible] = useState(true);
  const DOWNLOAD_URL = import.meta.env.VITE_DOWNLOAD_URL || "";

  const stats = useMemo(() => {
    const baseStats = [
      { label: "감정", count: 0 },
      { label: "음량", count: 0 },
      { label: "속도", count: 0 },
      { label: "톤", count: 0 },
    ];
    if (!result || !result.output?.utterances) return baseStats;
    const utterances = result.output.utterances;
    const highlightedUtterances = utterances.filter((utterance) => {
      let instanceCount =
        (utterance.emotion !== null ? 1 : 0) +
        (utterance.voice_highlight?.loudness?.loudness_outliers?.length || 0) +
        (utterance.voice_highlight?.pitch?.pitch_outliers?.length || 0) +
        (utterance.speed_info?.speech_level &&
        utterance.speed_info.speech_level !== "0"
          ? 1
          : 0);
      return instanceCount >= 2;
    });
    const emotionCount = highlightedUtterances.filter(
      (u) => u.emotion !== null
    ).length;
    const loudnessCount = highlightedUtterances.reduce(
      (acc, cur) =>
        acc + (cur.voice_highlight?.loudness?.loudness_outliers?.length || 0),
      0
    );
    const pitchCount = highlightedUtterances.reduce(
      (acc, cur) =>
        acc + (cur.voice_highlight?.pitch?.pitch_outliers?.length || 0),
      0
    );
    const speedCount = highlightedUtterances.filter(
      (u) => u.speed_info?.speech_level && u.speed_info.speech_level !== "0"
    ).length;
    return baseStats.map((stat) => {
      if (stat.label === "감정") return { ...stat, count: emotionCount };
      if (stat.label === "음량") return { ...stat, count: loudnessCount };
      if (stat.label === "속도") return { ...stat, count: speedCount };
      if (stat.label === "톤") return { ...stat, count: pitchCount };
      return stat;
    });
  }, [result]);

  const totalHighlights = useMemo(() => {
    if (!result?.output?.utterances) return 0;
    const utterances = result.output.utterances;
    return utterances.filter((utterance) => {
      let instanceCount =
        (utterance.emotion !== null ? 1 : 0) +
        (utterance.voice_highlight?.loudness?.loudness_outliers?.length || 0) +
        (utterance.voice_highlight?.pitch?.pitch_outliers?.length || 0) +
        (utterance.speed_info?.speech_level &&
        utterance.speed_info.speech_level !== "0"
          ? 1
          : 0);
      return instanceCount >= 3;
    }).length;
  }, [result]);

  const markers = useMemo(() => {
    if (!result?.output?.utterances) return [];
    const allMarkers: MarkerData[] = [];
    result.output.utterances.forEach((utterance: OutputUtterance) => {
      let instanceCount =
        (utterance.emotion !== null ? 1 : 0) +
        (utterance.speed_info?.speech_level &&
        utterance.speed_info.speech_level !== "0"
          ? 1
          : 0) +
        (utterance.voice_highlight?.loudness?.loudness_outliers?.length || 0) +
        (utterance.voice_highlight?.pitch?.pitch_outliers?.length || 0);

      if (instanceCount >= 3)
        allMarkers.push({
          start: utterance.start,
          end: utterance.end,
          type: "highlight",
          text: utterance.text,
          speaker: utterance.speaker,
        });
      if (utterance.emotion !== null)
        allMarkers.push({
          start: utterance.start,
          end: utterance.end,
          type: "emotion",
          text: utterance.text,
          speaker: utterance.speaker,
        });
      if (
        utterance.speed_info?.speech_level &&
        utterance.speed_info.speech_level !== "0"
      )
        allMarkers.push({
          start: utterance.start,
          end: utterance.end,
          type: "speed",
          text: utterance.text,
          speaker: utterance.speaker,
        });
      const vh = utterance.voice_highlight;
      if (vh) {
        const loudnessMarkers =
          vh.loudness?.loudness_outliers?.map((o: Outlier) => ({
            ...o,
            type: "loudness" as const,
            text: utterance.text,
            speaker: utterance.speaker,
          })) || [];
        const pitchMarkers =
          vh.pitch?.pitch_outliers?.map((o: Outlier) => ({
            ...o,
            type: "pitch" as const,
            text: utterance.text,
            speaker: utterance.speaker,
          })) || [];
        allMarkers.push(...loudnessMarkers, ...pitchMarkers);
      }
    });
    return allMarkers;
  }, [result]);

  const audioInfo = useMemo(() => {
    if (!result)
      return { src: undefined, duration: 0, type: undefined as string | undefined };

    const parseDuration = (value: unknown): number => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === "string") {
        const numeric = Number(value);
        if (!Number.isNaN(numeric) && numeric > 0) {
          return numeric;
        }

        if (value.includes(":")) {
          const parts = value.split(":").map((part) => Number(part));
          if (parts.every((part) => !Number.isNaN(part))) {
            return parts.reverse().reduce((acc, part, index) => acc + part * Math.pow(60, index), 0);
          }
        }
      }

      return 0;
    };

    const durationCandidates: unknown[] = [
      result.voice_highlight?.audio_duration,
      result.signal_processing?.audio_info?.duration,
      result.signal_processing?.media_info?.duration,
      result.output?.mediaInfo?.duration,
      result.speech2text?.audio_info?.duration,
    ];

    const durationFromCandidates =
      durationCandidates.reduce<number>((acc, candidate) => {
        if (acc > 0) return acc;
        const parsed = parseDuration(candidate);
        return parsed > 0 ? parsed : acc;
      }, 0) ?? 0;

    const durationFromUtterances =
      result.output?.utterances?.reduce<number>((acc, utterance) => {
        const parsed = parseDuration(utterance.end);
        return parsed > acc ? parsed : acc;
      }, 0) ?? 0;

    const duration =
      durationFromCandidates > 0 ? durationFromCandidates : durationFromUtterances;

    const rawSrc =
      result.signal_processing?.audio_path ||
      result.signal_processing?.media_path ||
      result.filePath;

    const normalisedSrc =
      rawSrc && rawSrc.startsWith("http")
        ? rawSrc
        : rawSrc && DOWNLOAD_URL
        ? `${DOWNLOAD_URL.replace(/\/+$/, "")}/${rawSrc.replace(/^\/+/, "")}`
        : rawSrc;

    const type =
      result.output?.mediaInfo?.codec_type ||
      result.signal_processing?.audio_info?.codec_type;

    return {
      src: normalisedSrc,
      duration,
      type,
    };
  }, [result, DOWNLOAD_URL]);

  const mllmInfo = useMemo(() => result?.output?.mllm || null, [result]);

  if (!result) {
    return <div>결과를 불러오는 중입니다...</div>;
  }

  const { src: audioSrc, duration: audioDuration } = audioInfo;

  // ... (나머지 핸들러 및 JSX 동일, stats, totalHighlights, markers, mllmInfo 사용)

  const handleStatClick = (label: string) => {
    setActiveMarkerType((prev) => (prev === label ? null : label));
  };

  const leftActions = [
    {
      type: "like" as ActionType,
      label: "",
      onClick: onLike,
      ...DEFAULT_ACTION_STYLES.like,
      isActive: feedbackStatus === "positive",
    },
    {
      type: "dislike" as ActionType,
      label: "",
      onClick: onDislike,
      ...DEFAULT_ACTION_STYLES.dislike,
      isActive: feedbackStatus === "negative",
    },
  ];

  const rightActions = [
    {
      label: "다운로드",
      type: "download" as ActionType,
      onClick: onDownload, // Use the passed-in onDownload prop
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  return (
    <PanelContainer>
      <ContentContainer>
        <S.TopSection>
          <S.LeftSection>
            <h2>Overview</h2>
            <S.OverviewText>
              이 음원은 {mllmInfo?.genre}로 판단되며, 총 {totalHighlights}개의
              주요 이벤트가 감지되었습니다. {mllmInfo?.highlights}
            </S.OverviewText>
            <S.RatioContainer>
              <S.Ratio>
                {totalHighlights}회
                <br />
                <small>총 하이라이트 수</small>
              </S.Ratio>
              <S.ToggleButton
                $isActive={isHighlightVisible}
                onClick={() => setIsHighlightVisible((prev) => !prev)}
              >
                하이라이트 <span>{isHighlightVisible ? "ON" : "OFF"}</span>
              </S.ToggleButton>
            </S.RatioContainer>
            <S.StatsTable>
              {stats.map((s, i) => (
                <S.StatCell
                  key={i}
                  $isFirst={i === 0}
                  onClick={() => handleStatClick(s.label)}
                  $isActive={activeMarkerType === s.label}
                >
                  <span className="label">{s.label}</span>
                  <span className="count">{s.count}</span>
                </S.StatCell>
              ))}
            </S.StatsTable>
          </S.LeftSection>
          <HighlightDonutChart
            labels={stats.map((s) => s.label)}
            values={stats.map((s) => s.count)}
          />
        </S.TopSection>
        {audioSrc && audioDuration > 0 && (
          <HighlightPlayer
            src={audioSrc}
            duration={audioDuration}
            markers={markers}
            activeMarkerType={activeMarkerType}
            isHighlightVisible={isHighlightVisible}
          />
        )}
      </ContentContainer>
      <CommonActionBar leftActions={leftActions} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default VoiceHighlightPanel;
